import type { CalendarPayload } from '@lms/shared-types'
import { apiRequest } from './client'

export interface CalendarTeamOption {
  id: string
  name: string
  headcount: number
}

export interface CalendarQuery {
  month: string
  team?: string | undefined
}

export function getCalendar(query: CalendarQuery): Promise<CalendarPayload> {
  const params = new URLSearchParams({ month: query.month })
  if (query.team) params.set('team', query.team)
  return apiRequest<CalendarPayload>(`/api/v1/calendar?${params.toString()}`)
}

export function listCalendarTeams(): Promise<CalendarTeamOption[]> {
  return apiRequest<CalendarTeamOption[]>('/api/v1/calendar/teams')
}

// ── Day-type classification ───────────────────────────────────────────────────

/**
 * How the backend classifies a calendar date.
 *
 * - weekday       → normal working day
 * - saturday      → Saturday (rest day in standard 5-day week)
 * - sunday        → Sunday (rest day)
 * - public_holiday → Malaysian public holiday (national or state-observed)
 */
export type DayKind = 'weekday' | 'saturday' | 'sunday' | 'public_holiday'

export interface DayType {
  /** ISO date string, e.g. "2026-05-03" */
  date: string
  kind: DayKind
  /** Populated only when kind === 'public_holiday' */
  holidayName?: string
}

/**
 * Classify each date in [from, to] as weekday / saturday / sunday /
 * public_holiday.
 *
 * Backend: GET /api/v1/calendar/day-types?from=&to=
 * Available to any authenticated employee.
 */
export function getDayTypes(from: string, to: string): Promise<DayType[]> {
  const qs = new URLSearchParams({ from, to }).toString()
  return apiRequest<DayType[]>(`/api/v1/calendar/day-types?${qs}`)
}
